// Autobahn (auto.de redesign) — 404 (Not Found), md3 re-skin.
//
// Flagged md3 version of NotFoundPage — used as the fallback route when isSearchNextEnabled().
// Same stores/logic (notFoundStore recommendations + stats, favoriteStore toggle) and the same
// NotFoundSearchWidget; recommendations render via the shared CarCard (see comment below).
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { toJS } from 'mobx';
import { Link } from 'react-router-dom';
import NotFoundSearchWidget from './NotFoundSearchWidget';
import { Button, Icon } from '@bytebrand/fe-ui-core-autobahn/ui';
import { CarCard, callDealerSalesPhone } from '@bytebrand/fe-ui-core-autobahn/cards';
import { toAutobahnCar } from '@bytebrand/fe-ui-core-autobahn/data/toAutobahnCar';
// same CarCard the homepage "Top-Angebote" / dashboard "Zuletzt angesehen" / SRP use — this
// page previously rendered the legacy VehicleSmallCard via CarsSlider, now brought in line with
// every other car listing surface (transformDataForVehicles output -> toAutobahnCar -> CarCard).
let NotFoundNextPage = class NotFoundNextPage extends React.Component {
    constructor() {
        super(...arguments);
        // guards against re-requesting the same car's gallery on repeated hover/touch
        Object.defineProperty(this, "imagesRequested", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "onRedirectTo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (path) => this.props.history.push(path)
        });
        Object.defineProperty(this, "onOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (storeCar) => this.props.history.push(`/search/vehicle/${storeCar.id}`)
        });
        /** Same target as onOpen, but for the card's "Angebot anfragen" CTA — tags the URL with
         *  `?requestOffer=1` (same query-flag convention as SearchNextPage.openCarRequestOffer), so
         *  the freshly-loaded VDP opens the guest/auth-gated offer flow (LOGIN_REGISTRATION_MODAL for
         *  a guest) instead of just landing on the page. */
        Object.defineProperty(this, "onRequestOffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (storeCar) => this.props.history.push(`/search/vehicle/${storeCar.id}?requestOffer=1`)
        });
        Object.defineProperty(this, "onCarFavorite", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => {
                const { favoriteStore, notFoundStore: { getStatsData } } = this.props;
                favoriteStore.toggleFavoriteCarStatus(id).then(() => getStatsData([id]));
            }
        });
        Object.defineProperty(this, "buildDeps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (storeCar) => {
                const { t, appStore: { language }, imagesStore, notFoundStore } = this.props;
                return {
                    getImageUrl: imagesStore.getSupportedImageFormat,
                    loadedImages: imagesStore.images.get(storeCar.id),
                    imageSize: 'small',
                    language,
                    t,
                    stats: notFoundStore.statsData.get(storeCar.id),
                };
            }
        });
        // gallery lazy-load on first hover/touch — parity with the SRP/homepage cards.
        Object.defineProperty(this, "loadCarImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (storeCar) => {
                const { imagesStore } = this.props;
                const id = storeCar && storeCar.id;
                if (!id || this.imagesRequested.has(id))
                    return;
                if (!storeCar.mainImageId || !storeCar.imagesCount || storeCar.imagesCount < 2)
                    return;
                if (imagesStore.images.get(id))
                    return;
                this.imagesRequested.add(id);
                Promise.resolve(imagesStore.getImages(id, false)).catch(() => { });
            }
        });
        Object.defineProperty(this, "renderCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { favoriteStore, notFoundStore } = this.props;
                const cars = toJS(notFoundStore.recommendationCars);
                return cars.map((storeCar) => (React.createElement(CarCard, { key: storeCar.id, car: toAutobahnCar(storeCar, this.buildDeps(storeCar)), isFavorite: favoriteStore.favoritesIDs.indexOf(storeCar.id) !== -1, onToggleFavorite: () => this.onCarFavorite(storeCar.id), onOpen: () => this.onOpen(storeCar), onMediaEnter: () => this.loadCarImages(storeCar), onRequestOffer: () => this.onRequestOffer(storeCar), onCall: callDealerSalesPhone, t: this.props.t })));
            }
        });
    }
    componentDidMount() {
        const { notFoundStore: { getRecommendationCars, getStatsData } } = this.props;
        getRecommendationCars().then(() => getStatsData());
    }
    render() {
        const { t, notFoundStore } = this.props;
        return (React.createElement("div", { className: "md3 nf-page" },
            React.createElement(Helmet, null,
                React.createElement("title", null, "Seite nicht gefunden (404) \u2013 Auto.de"),
                React.createElement("meta", { name: "robots", content: "noindex,follow" })),
            React.createElement("div", { className: "container nf-hero" },
                React.createElement("div", { className: "nf-hero-text" },
                    React.createElement("span", { className: "nf-eyebrid" },
                        React.createElement(Icon, { name: "search-x", size: 20 }),
                        " Fehler 404"),
                    React.createElement("h1", { className: "nf-code" }, "404"),
                    React.createElement("p", { className: "nf-sub" }, t('notFoundSubtext')),
                    React.createElement(Link, { to: "/" },
                        React.createElement(Button, { variant: "filled", icon: "home" }, t('toHomePage')))),
                React.createElement("div", { className: "nf-search" },
                    React.createElement(NotFoundSearchWidget, { onRedirect: this.onRedirectTo }))),
            React.createElement("div", { className: "container nf-reco" },
                React.createElement("h2", { className: "md-title" }, t('recommendationTopOffer')),
                React.createElement("div", { className: "cards-offers" }, notFoundStore.recommendationCars.length ? this.renderCars() : null))));
    }
};
NotFoundNextPage = __decorate([
    inject('appStore', 'favoriteStore', 'notFoundStore', 'imagesStore'),
    observer
], NotFoundNextPage);
export default translate(['NotFoundPage', 'vehicleProps'])(NotFoundNextPage);
